/**
 * The OData plugin module
 * @module component/edm-odata/index
 */
import _ from 'underscore';
import {
  hasOwnProperty,
  defineConstProperty,
  defineProducedPropertyOnClass,
} from '@bingads-webui/reflection';

import { odata } from '@bingads-webui/http-util';
import * as url from '@bingads-webui/url-util';
import { resIdsPlugin } from '@bingads-webui/edm-resource-identifiers-legacy';

/**
 * @param {EDM} edm - The EDM object to apply the plugin to
 * @param {String} rootURL - The root URL of the OData service
 * @return {Void} nothing to return
 */
export function odataPlugin(edm, rootURL, {
  processError = _.identity,
  processOptions = _.identity,
} = {}) {
  const odataRootURL = rootURL.replace(/\/*$/, '');
  if (hasOwnProperty(edm, 'odata')) {
    return;
  }
  const rethrowError = (err) => {
    throw processError(err);
  };

  resIdsPlugin(edm);

  /**
   * @name edm
   * @property {Object} odata - A dummy indicator for odataPlugin being applied
   */
  defineConstProperty(edm, 'odata', (() => {
    const {
      ResourceIdentifier,
      Navigation,
    } = edm.resourceIdentifiers;

    defineProducedPropertyOnClass(
      ResourceIdentifier, 'odataURL',
      /**
       * @memberof ResourceIdentifier#
       * @this ResourceIdentifier
       * @returns {String} The URL to access the OData resource
       */
      function () { // eslint-disable-line func-names
        return url.join(odataRootURL, this.path);
      }
    );

    function getCollection({
      top = 20,
      count = true,
      // Contact zhbliu@microsoft.com for adding more options here
      // TODO:
      //   filter, select (auto use $expand when nessasary), orderby
      //   we should avoid random query params like SegmentationTypes, startDate, endDate, etc
    } = {}) {
      return odata.get(this.odataURL, processOptions({
        data: {
          $top: top,
          $count: count,
        },
      })).catch(rethrowError);
    }

    function getItem() {
      return odata.get(this.odataURL, processOptions()).catch(rethrowError);
    }

    // edm.currentCustomer.Accounts is an instance of ResourceIdentifier
    // usage:
    // edm.currentCustomer.Accounts.get({ data: { $top: 20 } })
    // TODO:
    //   * type enrichment, for example, 'Active' => Enum.CampaignStatus'Active'
    //   * type check
    //   * cache and onChange event on response
    ResourceIdentifier.prototype.get = function get(options) {
      return this.type instanceof edm.types.CollectionType ?
        getCollection.call(this, options) :
        getItem.call(this, options);
    };

    // usage:
    // edm.currentCustomer.Accounts.findAll({ limit: 20 }), align with jsdata
    // TODO:
    //   * refine available options, jsdata aligned arguments
    //   * jsdata aligned response
    ResourceIdentifier.prototype.findAll = function findAll(query) {
      return this.get({ top: query.limit });
    };

    defineProducedPropertyOnClass(
      Navigation, 'odataURL',
      /**
       * @memberof Navigation#
       * @this Navigation
       * @returns {String} The URL to access the OData resource
       */
      function () { // eslint-disable-line func-names
        return url.join(odataRootURL, this.path);
      }
    );

    // put the plugin indicator
    return {};
  })());
}
